// Copyright (c) 2024 Readymadeui
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import LinkInput from './LinkInput';
import TextUserPair from './TextUserPair';
import CopyTextButton from './CopyTextButton';
import DotSpinner from './DotSpinner';
import Tabs from './Tabs';
import ArtPage from './ArtPage';
import getIconForLink from '../iconForLink.js';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import ArtTabs from './ArtTabs';
import 'delayed-scroll-restoration-polyfill';
import ArtThumbnail from './ArtThumbnail.js';
import { SegmentedControl, TextInput, ActionIcon } from '@mantine/core';
import { formatYear } from './../utils.js'; // Import the global function

// Move cache outside the component to persist across renders
const cache = new Map();

const AddEvent = ({repeatedEnter = false}) => {
  const myUser = useContext(MyUserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [art, setArt] = useState(null);
  const [addedEvents, setAddedEvents] = useState([]);
  // const [successSign, setSuccessSign] = useState(null); // like, todo, finished

  const [searching, setSearching] = useState(false);
  //const [foundArts, setFoundArts] = useState([]);

  // const [foundArtsByType, setFoundArtsByType] = useState(new Map());

  const [foundMovies, setFoundMovies] = useState(null);
  const [foundBooks, setFoundBooks] = useState(null);
  const [foundGames, setFoundGames] = useState(null);

  const searchInputRef = useRef(null);
  const [renderCount, setRenderCount] = useState(0);
  useEffect(() => {
    // alert("repeatedEnter=" + repeatedEnter);
    // Focus the input when the component mounts or when the user navigates back to this page
    if (searchInputRef.current && repeatedEnter) {
      searchInputRef.current.focus();
    }
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const searchTerm = queryParams.get('term');
    const limit = queryParams.get('limit') || 20;
    // const author = queryParams.get('author');
    if (searchTerm) {
    // if (searchTerm || author) {
      // if (author) {
      //   searchTerm = "author:"+author;
      // }
      handleSearchArt(searchTerm, "movie", limit);
      handleSearchArt(searchTerm, "book", limit);
      handleSearchArt(searchTerm, "videoGame", limit);
      searchInputRef.current.value = searchTerm;
    }
  }, []);

  useEffect(() => {
    // Enable scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  const [enterPressed, setEnterPressed] = useState(false); // to prevent suggest flickering when response for old request comes

  const typingSearchClearTimeoutRef = useRef(null);
  const [searchSuggest, setSearchSuggest] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const userInitiatedFocusRef = useRef(false);
  const swipeTouchXRef = useRef(0);
  const swipeTouchYRef = useRef(0);
  const swipeActiveRef = useRef(false);
  const handleSearchSuggest = (term) => {
    axiosInstance
    .get(`/arts/searchSuggest?term=${term}`)
    .then((response) => {
      // console.log("term: " + term + ", searchInputRef.current.value: " + searchInputRef.current.value);
      if (searchInputRef.current.value !== term) { // late response // TODO: move into the component's main code
        return;
      }
      setSearchSuggest(response.data);
    })
    .catch((error) => {
      console.log(error);
    })
  };
  const handleSearchHistory = () => {
    axiosInstance
    .get(`/arts/searchHistory`)
    .then((response) => {
      setSearchHistory(response.data || []);
    })
    .catch((error) => {
      console.log(error);
    })
  };

  const handleSearchArt = (term, artType, limit=25) => {
    if (term === "") {
      return;
    }

    const cacheKey = `${term}-${artType}`;
    const cachedResult = cache.get(cacheKey);

    // Remove stale data from cache
    for (const [key, value] of cache.entries()) {
      if (Date.now() - value.timestamp >= 10 * 60 * 1000) { // 10 minutes
        cache.delete(key);
      }
    }

    if (cachedResult && Date.now() - cachedResult.timestamp < 10 * 60 * 1000) {
      const data = cachedResult.data;
      if (artType === "movie") {
        setFoundMovies(data);
      } else if (artType === "book") {
        setFoundBooks(data);
      } else {
        setFoundGames(data);
      }
      return;
    }

    setSearching(true);
    setFoundMovies(null);
    setFoundGames(null);
    setFoundBooks(null);
    setArt(null);


    axiosInstance
      .get(`/arts/search?term=${term}&artType=${artType}&limit=${limit}`)
      .then((response) => {
        setSearching(false);
        setSearchSuggest(null);

        if (addedEvents.length > 0) {
          return;
        }

        const data = response.data;
        cache.set(cacheKey, { data, timestamp: Date.now() });

        if (artType === "movie") {
          setFoundMovies(data);
        } else if (artType === "book") {
          setFoundBooks(data);
        } else {
          setFoundGames(data);
        }
      })
      .catch((error) => {
        console.error(error);
        setSearching(false);
        if (error.response.status === 400) {
          alert(error.response.data);
        }
      });
  };

  const isMobileDeviceExceptAndroid = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iphone|ipad|ipod|windows phone|blackberry/i.test(userAgent);
  };

  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().includes('firefox');
  };

  const handleArtSubmit = (link) => {
    axiosInstance
    .post(`/arts`, { wikiArticleLink: link })
    .then((response) => {
      // setAddedEvents(prevObjects => [response.data, ...prevObjects]); // not sure
      // setFoundMovies(null);
      // setFoundGames(null);
      // setFoundBooks(null);
      // setActiveTab('movie');
      //setArt(response.data);
      navigate("/arts/"+response.data.id);
      // setLinkInput(""); // not sure
      // setSuccessSign(eventType);
    })
    .catch((error) => {
      console.error(error);
      // setSuccessSign(null);
      // setLinkInputErr(true);
    });
  };

  // Determine the tab to show based on found arts.
  // Expects non-null results and exactly one tab with results.
  const determineTabBasedOnResults = () => {
    let res;
    if (foundMovies.length > 0) {
      res = 'movie';
    } else if (foundBooks.length > 0) {
      res = 'book';
    } else if (foundGames.length > 0) {
      res = 'videoGame';
    }
    return res;
  };

  const getActiveTabFromURL = () => {
    const p = new URLSearchParams(location.search).get('tab');
    return (p === 'book' || p === 'videoGame') ? p : 'movie';
  };
  const [activeTab, setActiveTabState] = useState(getActiveTabFromURL);
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`/addEvent?${params.toString()}`, { replace: true });
  };

  // Update activeTab dynamically when arts are fetched
  useEffect(() => {
    if (!foundMovies || !foundBooks || !foundGames) { // too early to determine tab
      return;
    }
    let nonZeroTabs = 0;
    if (foundMovies.length > 0) nonZeroTabs++;
    if (foundBooks.length > 0) nonZeroTabs++;
    if (foundGames.length > 0) nonZeroTabs++;
    if (nonZeroTabs == 1) {
      setActiveTab(determineTabBasedOnResults());
    }
  }, [foundMovies, foundBooks, foundGames]);

  const foundArtContainerByTab = (tab) => {
    if (tab === "movie") {
      return foundMovies;
    } else if (tab === "book") {
      return foundBooks;
    } else if (tab === "videoGame") {
      return foundGames;
    } else {
      alert("Unexpected tab");
      return foundMovies;
    }
  }

  const eventTypeStr = (i) => {
    const m = {
      0: "none",
      1: "finished",
      2: "todo",
      3: "like"
    };
    return m[i];
  }

  const getTrendingCategoryFromLocation = () => {
    const p = new URLSearchParams(location.search).get('cat');
    return (p === 'book' || p === 'videoGame') ? p : 'movie';
  };
  const trendingCategory = getTrendingCategoryFromLocation();
  const trendingCategoryRef = useRef(trendingCategory);
  trendingCategoryRef.current = trendingCategory;

  const setTrendingCategory = (cat) => {
    trendingCategoryRef.current = cat;
    const params = new URLSearchParams(location.search);
    params.set('cat', cat);
    navigate(`/addEvent?${params.toString()}`);
  };

  // const [showTrending, setShowTrending] = useState(true); // if false, recommendations are shown
  const [trendingMovies, setTrendingMovies] = useState(null);
  const [trendingMoviesUpdatedTime, setTrendingMoviesUpdatedTime] = useState(null);
  const fetchTrendingMovies = () => {
    const queryParams = new URLSearchParams(location.search);
    const popMostRecent = queryParams.get('popMostRecent');

    return axiosInstance
      .get(`/trendingMovies${popMostRecent ? `?popMostRecent=${popMostRecent}` : ''}`)
      .then((response) => {
        setTrendingMovies(response.data.arts);
        setTrendingMoviesUpdatedTime(response.data.updatedTime);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    (async () => {
      await fetchTrendingMovies();
      handleSearchHistory();
    })();
  }, []);

  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const showSuggestList = !enterPressed && searchSuggest && searchSuggest.length > 0;
  const showHistoryList = showSearchHistory && (!searchTerm || searchTerm.length === 0) && searchHistory && searchHistory.length > 0;
  const categoryLabels = {
    movie: language === "ru" ? "Фильмы" : "Movies",
    book: language === "ru" ? "Книги" : "Books",
    videoGame: language === "ru" ? "Игры" : "Video Games"
  };
  const categoryOrder = ["movie", "book", "videoGame"];
  const categoryEnum = {
    movie: 1,
    videoGame: 2,
    book: 3
  };
  const filterByCategory = (arts, category) => {
    if (!arts) {
      return [];
    }
    return arts.filter((item) => item.category === categoryEnum[category]);
  };

  const formatUpdatedAgo = (updatedTime) => {
    if (!updatedTime) {
      return "";
    }
    const now = Date.now();
    const updatedAt = new Date(updatedTime).getTime();
    let diffMinutes = Math.max(0, Math.floor((now - updatedAt) / 60000));
    if (diffMinutes < 60) {
      const unit = language === "ru" ? "мин" : (diffMinutes === 1 ? "minute" : "minutes");
      return language === "ru"
        ? `Обновлено ${diffMinutes} ${unit} назад`
        : `Updated ${diffMinutes} ${unit} ago`;
    }
    const diffHoursTotal = Math.floor(diffMinutes / 60);
    const ruPluralHour = (count) => {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) {
        return "час";
      }
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return "часа";
      }
      return "часов";
    };
    if (diffHoursTotal < 24) {
      const unit = language === "ru" ? ruPluralHour(diffHoursTotal) : (diffHoursTotal === 1 ? "hour" : "hours");
      return language === "ru"
        ? `Обновлено ${diffHoursTotal} ${unit} назад`
        : `Updated ${diffHoursTotal} ${unit} ago`;
    }
    const diffDays = Math.floor(diffHoursTotal / 24);
    const diffHoursRemainder = diffHoursTotal % 24;
    const dayUnit = language === "ru" ? "дн" : (diffDays === 1 ? "day" : "days");
    const hourUnit = language === "ru" ? ruPluralHour(diffHoursRemainder) : (diffHoursRemainder === 1 ? "hour" : "hours");
    if (diffHoursRemainder === 0) {
      return language === "ru"
        ? `Обновлено ${diffDays} ${dayUnit} назад`
        : `Updated ${diffDays} ${dayUnit} ago`;
    }
    return language === "ru"
      ? `Обновлено ${diffDays} ${dayUnit} ${diffHoursRemainder} ${hourUnit} назад`
      : `Updated ${diffDays} ${dayUnit} ${diffHoursRemainder} ${hourUnit} ago`;
  };

  const performSearch = (term) => {
    const nextTerm = term.trim();
    if (nextTerm === "") {
      return;
    }

    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
    setEnterPressed(true);
    setSearchSuggest(null);
    setShowSearchHistory(false);
    setSearchTerm(nextTerm);
    if (searchInputRef.current) {
      searchInputRef.current.value = nextTerm;
    }

    if (nextTerm.startsWith("https://")) {
      handleArtSubmit(nextTerm);
      return;
    }

    handleSearchArt(nextTerm, "movie");
    handleSearchArt(nextTerm, "book");
    handleSearchArt(nextTerm, "videoGame");

    navigate(`/addEvent?term=${encodeURIComponent(nextTerm)}&tab=${activeTab}`, { replace: true });
  };

  return (
    <div className="w-full px-0 md:px-1 z-1">
      <div>
        <div className="mb-1 mt-1 max-w-md mx-auto">
          <TextInput
            ref={searchInputRef}
            size="lg"
            radius="xl"
            placeholder={language === "ru" ? "Поиск по фильмам, книгам и играм..." : "Search movies, books, or games..."}
            value={searchTerm}
            onKeyDown={(e) => {
              setSearchSuggest(null);
              setShowSearchHistory(false);
              if (e.key === 'Enter') {
                performSearch(e.target.value);
              }
            }}
            onChange={(e) => {
              setEnterPressed(false);
              setShowSearchHistory(false);
              setSearchTerm(e.target.value);
              clearTimeout(typingSearchClearTimeoutRef.current);
              typingSearchClearTimeoutRef.current = setTimeout(() => {
                handleSearchSuggest(e.target.value);
              }, 90);
            }}
            onMouseDown={() => { userInitiatedFocusRef.current = true; }}
            onTouchStart={() => { userInitiatedFocusRef.current = true; }}
            onPointerDown={() => { userInitiatedFocusRef.current = true; }}
            onFocus={() => {
              if (userInitiatedFocusRef.current) {
                setShowSearchHistory(true);
              }
              userInitiatedFocusRef.current = false;
            }}
            onBlur={() => {
              setTimeout(() => { setShowSearchHistory(false); }, 150);
            }}
            leftSection={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.904 192.904" width="16px" style={{ fill: "var(--mantine-color-dimmed)" }}>
                <path d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z" />
              </svg>
            }
            rightSection={
              (isMobileDeviceExceptAndroid() || isFirefox()) && searchTerm ? (
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current.value = '';
                  searchInputRef.current.focus();
                }}>
                  &times;
                </ActionIcon>
              ) : null
            }
          />
        </div>
        <div className="flex justify-center">
          <div className="absolute z-50 rounded-md max-w-md mx-auto w-full" style={{ background: "var(--mantine-color-body)", border: (showSuggestList || showHistoryList) ? "1px solid var(--mantine-color-default-border)" : "none" }}>
            {showSuggestList && searchSuggest.map((suggest, index) => (
              <div
                key={suggest.imageUrl}
                className={"p-2 cursor-pointer flex items-center" + (index===0 ? " rounded-t-md" : "") + (index === searchSuggest.length-1 ? " rounded-b-md" : "")}
                style={{ background: "var(--mantine-color-body)" }}
                onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                onMouseOut={(e) => e.currentTarget.style.background = "var(--mantine-color-body)"}
                onClick={() => {
                  performSearch(suggest.text);
                }}
              >
                {suggest.text}
              </div>
            ))}
            {showHistoryList && searchHistory.map((term, index) => (
              <div
                key={`${term}-${index}`}
                className={"p-2 cursor-pointer flex items-center" + (index===0 ? " rounded-t-md" : "") + (index === searchHistory.length-1 ? " rounded-b-md" : "")}
                style={{ background: "var(--mantine-color-body)" }}
                onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                onMouseOut={(e) => e.currentTarget.style.background = "var(--mantine-color-body)"}
                onClick={() => {
                  performSearch(term);
                }}
              >
                {term}
              </div>
            ))}
          </div>
        </div>
      </div>      

      {
      (foundMovies || foundBooks || foundGames) &&
      <div className="my-2">
        <ArtTabs 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          language={language} 
          movies={foundMovies} 
          books={foundBooks} 
          games={foundGames} 
                  />
      </div>
      }

      {
        searching && 
        <div className="flex justify-center">
          <DotSpinner />
        </div>
      }

      <div className="flex justify-center">
        <div id={activeTab} className="grid grid-cols-4 gap-3">
          {foundArtContainerByTab(activeTab) && foundArtContainerByTab(activeTab).map((foundArt, index) => (
            <div id={foundArt.pageUrl} className="hover:shadow-lg transition-shadow duration-200">
              <div>
                <div id={foundArt.pageUrl+"0"} className="flex justify-center">
                  <Link to={foundArt.sourceArt ? "/arts/"+foundArt.sourceArt.id : "/artByLink?link="+foundArt.pageUrl}>
                    <ArtThumbnail art={foundArt.sourceArt ? foundArt.sourceArt : {thumbnailUrl: foundArt.thumbUrl}} />
                  </Link>
                </div>
                <div className="flex justify-center items-center text-center w-full h-auto text-sm font-medium mt-2">
                  {( (language === "en" || !foundArt.ruName) ? foundArt.enName : foundArt.ruName) + formatYear(foundArt.year, language)}
                </div>
                {activeTab === "book" && foundArt.author && (
                  <div className="flex justify-center items-center text-center w-full h-auto text-xs mt-1" style={{ color: "var(--mantine-color-dimmed)" }}>
                    {foundArt.author}
                  </div>
                )}
                <div className="flex justify-center mt-1">
                {
                  foundArt.sourceArt ?
                    <PlatformRatings art={foundArt.sourceArt} />
                  :
                    foundArt.ratings &&
                      <PlatformRatings art={foundArt.ratings} />
                }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {trendingMovies && !(foundMovies || foundBooks || foundGames) && !searchTerm && (
        <div className="mt-4">
          <div className="flex justify-center mb-2">
            <SegmentedControl
              value={trendingCategory}
              onChange={setTrendingCategory}
              data={categoryOrder.map(c => ({ label: categoryLabels[c], value: c }))}
              size="sm"
              withItemsBorders={false}
              styles={{ root: { border: 'none' }, control: { border: 'none' } }}
            />
          </div>
          <div className="flex justify-center text-xs text-center mb-2" style={{ color: "var(--mantine-color-dimmed)" }}>
            {formatUpdatedAgo(trendingMoviesUpdatedTime)}
          </div>
          <div
            onTouchStart={(e) => {
              swipeTouchXRef.current = e.touches[0].clientX;
              swipeTouchYRef.current = e.touches[0].clientY;
              swipeActiveRef.current = false;
            }}
            onTouchMove={(e) => {
              const dx = e.touches[0].clientX - swipeTouchXRef.current;
              const dy = e.touches[0].clientY - swipeTouchYRef.current;
              if (!swipeActiveRef.current && Math.abs(dx) > 20 && Math.abs(dx) > Math.abs(dy) * 1.2) {
                swipeActiveRef.current = true;
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeActiveRef.current) return;
              e.preventDefault();
              const dx = e.changedTouches[0].clientX - swipeTouchXRef.current;
              const idx = categoryOrder.indexOf(trendingCategoryRef.current);
              if (dx < -40 && idx < categoryOrder.length - 1) {
                setTrendingCategory(categoryOrder[idx + 1]);
              }
              if (dx > 40 && idx > 0) {
                setTrendingCategory(categoryOrder[idx - 1]);
              }
            }}
            style={{ minHeight: 100, touchAction: 'pan-y' }}
          >
            {(() => {
              const items = filterByCategory(trendingMovies, trendingCategory);
              if (items.length === 0) return null;
              return (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                  {items.map((art) => {
                    const localized = localizeArt(art, language);
                    return (
                      <div key={art.id}>
                        <Link to={`/arts/${art.id}`}>
                          <ArtThumbnail art={art} />
                        </Link>
                        <div className="text-center text-xs font-medium mt-0.5 px-0.5 line-clamp-2">
                          {localized.name + formatYear(art.year, language)}
                        </div>
                        <div className="flex justify-center">
                          <PlatformRatings art={art} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {filterByCategory(trendingMovies, trendingCategory).length === 0 && (
              <div className="flex justify-center items-center py-12" style={{ color: "var(--mantine-color-dimmed)" }}>
                {language === "ru" ? "Скоро появятся" : "Coming soon"}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default AddEvent;
